/**
 * Market signal slug → label helpers — Welle 3: fully i18n-driven.
 *
 * Single source of truth for both `MarketSignalBadge` (compact, on
 * DealCards) and the full block on `ListingDetailPage`. ALL wording
 * lives in the `market` locale namespace (de/en/hr) — this file only
 * contains slug → key routing logic. Keep the slug set in sync with
 * `app/services/market/signal.py`.
 */
import i18n from "../i18n";

const t = (key, opts) => i18n.t(`market:${key}`, opts);

/* ----------------- slug → phrase resolvers ----------------- */

/** Strict signal slug → long phrase ("deutlich unter Marktpreis"). */
export function signalPhrase(slug) {
  if (!slug) return null;
  return t(`signal.${slug}`, { defaultValue: slug });
}

function softVariantKey(softSignal) {
  if (!softSignal) return null;
  if (softSignal === "fair_soft") return "fair";
  const strong =
    softSignal.includes("strong") || softSignal.endsWith("weak_cohort");
  if (softSignal.startsWith("under_market")) {
    return strong ? "under_strong" : "under";
  }
  if (softSignal.startsWith("over_market")) {
    return strong ? "over_strong" : "over";
  }
  return null;
}

/** Soft signal slug → verbose phrase (DetailPage wording). */
export function softPhrase(slug) {
  const key = softVariantKey(slug);
  if (!key) return slug || null;
  return t(`softFull.${key}`);
}

/** Caveat slug → verbose directional phrase. */
export function caveatPhrase(slug) {
  if (!slug) return null;
  return t(`caveat.${slug}`, { defaultValue: t("caveat.fallback") });
}

/* ----------------- helpers ----------------- */

/** Rounded absolute integer of price_vs_market_pct (−12.34 → "12"). */
export function formatPctAbs(pct) {
  if (pct == null || Number.isNaN(pct)) return null;
  return String(Math.round(Math.abs(pct)));
}

/** Confidence band → localized wording for the tooltip.
 * Accepts (in priority order):
 *   * the Welle-3 `confidence_band_key` slug ("high" | "medium" |
 *     "cautious" | "weak") — translated locally;
 *   * a legacy pre-rendered label string (shown verbatim);
 *   * a legacy numeric score (banded locally). */
export function confidenceBand(value) {
  if (typeof value === "string" && value) {
    if (i18n.exists(`market:confidence.${value}`)) {
      return t(`confidence.${value}`);
    }
    return value; // legacy server-rendered label
  }
  if (value == null) return t("confidence.unknown");
  if (value >= 0.6) return t("confidence.high");
  if (value >= 0.3) return t("confidence.medium");
  return t("confidence.low");
}

/**
 * Phase H2-followup — cohort level label. Returns a short localized
 * phrase; unknown / null → generic fallback so the row still reads
 * naturally.
 */
export function cohortLabel(level) {
  if (!level) return t("cohort.fallback");
  return t(`cohort.${level}`, { defaultValue: t("cohort.fallback") });
}

/**
 * Short cohort-scope label used in the subtitle line below the badge.
 * Returns `null` for unknown levels so joins drop them cleanly.
 */
export function cohortShortLabel(level) {
  if (!level) return null;
  const label = t(`cohort.${level}`, { defaultValue: "" });
  return label || null;
}

/** Welle 3 — subtitle: prefer the translatable key, fall back to the
 * legacy server-rendered German text (older cached payloads). */
export function resolveSubtitle(card) {
  if (!card) return null;
  const key = card.market_signal_subtitle_key;
  if (key) {
    return t(`subtitle.${key}`, {
      defaultValue: card.market_signal_subtitle || null,
    });
  }
  return card.market_signal_subtitle || null;
}

function resolveWarning(card) {
  const key = card && card.user_facing_warning_key;
  if (key) {
    return t(`warning.${key}`, {
      defaultValue: card.user_facing_warning || t("caveat.fallback"),
    });
  }
  return (card && card.user_facing_warning) || t("caveat.fallback");
}

/** Tooltip line — `Basis: N vergleichbare Inserate · <band>`. */
export function buildTooltip(comparable_count, confidence_value) {
  const n = comparable_count;
  const band = confidenceBand(confidence_value);
  if (n == null || n === 0) return t("tooltipBasisNone", { band });
  if (n === 1) return t("tooltipBasisOne", { band });
  return t("tooltipBasis", { count: n, band });
}

/** Strict signal → bold label, with signed pct. */
export function formatStrictLabel(signal, pct) {
  const abs = formatPctAbs(pct);
  if (signal === "fair") return t("strict.fair");
  if (signal === "strong_under_market" || signal === "under_market") {
    return abs ? t("strict.under_pct", { pct: abs }) : t("strict.under");
  }
  if (signal === "over_market" || signal === "strongly_over_market") {
    return abs ? t("strict.over_pct", { pct: abs }) : t("strict.over");
  }
  return signalPhrase(signal);
}

/** Soft signal → muted label. Pct included only when it's meaningful.
 *
 * Card-mode (`compact=true`, default) keeps the label short;
 * DetailPage-mode (`compact=false`) keeps the verbose phrasing. The
 * `≈` glyph the badge prepends carries the "tendency" meaning in
 * compact mode.
 */
export function formatSoftLabel(softSignal, pct, opts = {}) {
  const compact = opts.compact !== false; // default true
  const abs = formatPctAbs(pct);
  const weakCohort = !!softSignal && softSignal.endsWith("weak_cohort");
  const key = softVariantKey(softSignal);
  if (!key) return softSignal || null;

  if (compact) {
    if (key === "fair") return t("softCompact.fair");
    const direction = t(`softCompact.${key}`);
    // V4.2 P0 Fix 5 — weak_cohort variants: direction-only wording.
    if (weakCohort || !abs) return direction;
    const sign = softSignal.startsWith("under_market") ? "−" : "+";
    return `${direction} ${sign}${abs}%`;
  }

  // Full (DetailPage) — verbose wording.
  const full = t(`softFull.${key}`);
  if (key === "fair" || !abs) return full;
  const sign = softSignal.startsWith("under_market") ? "−" : "+";
  return `${full} · ${sign}${abs}%`;
}

/**
 * Caveat slug → COMPACT phrase for the DealCard pill (≤ 19 chars).
 * Never includes a raw percentage; the verbose data-basis caveat lives
 * in the tooltip.
 */
export function formatCaveatLabelCompact(caveatSlug) {
  return t(`caveatCompact.${caveatSlug}`, {
    defaultValue: t("caveatCompact.fallback"),
  });
}

/** Caveat slug → FULL phrase for the ListingDetailPage:
 * "direction · subtitle". `subtitleText` should already be localized
 * (use `resolveSubtitle(card)`). */
export function formatCaveatLabelFull(caveatSlug, subtitleText) {
  const direction = caveatPhrase(caveatSlug) || caveatSlug;
  if (subtitleText) return `${direction} · ${subtitleText}`;
  return direction;
}

/**
 * V4.2 P0 Fix 1 + Fix 5 + Fix 6 — "Pool size labelled" line below the
 * badge. Broad/weak cohorts NEVER show the raw numeric count; tight
 * cohorts show "{N} Vergleiche · {cohort} · {subtitle}".
 */
const BROAD_COHORT_LEVELS = new Set([
  // Auto
  "same_brand_only",
  "family_tier_wide",
  "same_model_family",
  "same_model_family_wide",
  "model_family",
  "model_brand",
  "unmatched",
  // Immo
  "same_region_or_postal_prefix",
  "country_family_wide",
  "object_type_exact",
]);

// V4.18 — location-scoped cohorts get a 📍 prefix in the cohort line.
const LOCATION_COHORTS = new Set([
  "radius_300m",
  "radius_750m",
  "same_postal_code",
  "same_city",
  "same_region_or_postal_prefix",
]);

const INSUFFICIENT_DATA_CAVEATS = new Set([
  "deutlich_unter_vergleich_geringe_datenbasis",
  "deutlich_ueber_vergleich_geringe_datenbasis",
  "insufficient_comparable_data",
]);

export function isWeakCohortContext(card) {
  if (!card) return false;
  // Mobile-Parity Sprint — backend-first display contract. When the
  // backend ships `comp_count_display_allowed` we trust it outright.
  if (card.comp_count_display_allowed === false) return true;
  if (card.comp_count_display_allowed === true) return false;
  if (BROAD_COHORT_LEVELS.has(card.cohort_level_name || "")) return true;
  // The engine's own "only a broad basis" verdict. Ships on every
  // surface (incl. the anonymous feed) even where `cohort_level_name`
  // is whitelisted away.
  if (card.market_signal_subtitle_key === "broad_cohort_conservative") return true;
  const soft = card.market_signal_soft || "";
  if (soft.endsWith("_weak_cohort")) return true;
  if (INSUFFICIENT_DATA_CAVEATS.has(card.market_signal_caveat || "")) return true;
  return false;
}

/**
 * The ONLY comparable count a user may ever see. `null` whenever the
 * backend blocks it (`comp_count_display_allowed === false`) or the
 * fallback heuristics detect a weak/broad basis — a blocked count must
 * not resurface in a tooltip, subtitle, explainer or card line.
 */
export function displayComparableCount(card) {
  if (!card) return null;
  if (card.comp_count_display_allowed === true) {
    const n = card.display_comp_count ?? card.comparable_count;
    return typeof n === "number" && n > 0 ? n : null;
  }
  if (card.comp_count_display_allowed === false) return null;
  if (isWeakCohortContext(card)) return null;
  const n = card.comparable_count;
  return typeof n === "number" && n > 0 ? n : null;
}

export function buildCohortSubtitle(card) {
  if (!card) return null;
  // Backend-first: `displayComparableCount` is the ONLY count users may
  // see; it returns null for every blocked/weak basis.
  const count = displayComparableCount(card);
  const cohortShort = cohortShortLabel(card.cohort_level_name);
  const subtitle = resolveSubtitle(card);
  const weak = isWeakCohortContext(card);

  if (weak) {
    // Broad / weak / insufficient — NEVER emit the raw count. Compose
    // qualitative wording only; the broad-basis subtitle leads when
    // present (slug-detected, language-independent).
    const warning = resolveWarning(card);
    const isBroadSubtitle =
      card.market_signal_subtitle_key === "broad_cohort_conservative" ||
      (card.market_signal_subtitle || "").toLowerCase().includes("breit");
    if (subtitle && isBroadSubtitle) return `${warning} · ${subtitle}`;
    return [warning, subtitle].filter(Boolean).join(" · ");
  }

  // Tight cohort — the numeric count is honest, show it.
  // V4.18 — explainable wording: "{N} sehr ähnliche Vergleichsobjekte
  // · 📍 300-m-Umkreis" instead of a bare "{N} Vergleiche".
  if (!count || count <= 0) return null;
  const countLabel =
    count === 1
      ? t("comparisonsSimilarOne")
      : t("comparisonsSimilar", { count });
  const scope = cohortShort
    ? LOCATION_COHORTS.has(card.cohort_level_name || "")
      ? `📍 ${cohortShort}`
      : cohortShort
    : null;
  return [countLabel, scope, subtitle].filter(Boolean).join(" · ");
}

/**
 * V4.18 — second tier below the cohort line: either the broader-layer
 * market validation ("Markt zusätzlich anhand von 152 Angeboten
 * derselben PLZ validiert") or the cascade-expansion hint.
 */
export function buildValidationLine(card) {
  if (!card) return null;
  if (card.market_validation_count > 0 && card.market_validation_level_name) {
    const scope = t(`validationScope.${card.market_validation_level_name}`, {
      defaultValue: t("validationScope.fallback"),
    });
    return t("validation.validated", {
      count: card.market_validation_count,
      scope,
    });
  }
  if (card.cohort_expanded && card.cohort_level_name) {
    const scope = cohortShortLabel(card.cohort_level_name);
    if (scope) return t("validation.expanded", { scope });
  }
  return null;
}

/* ----------------- valuation tier (single gate) ----------------- */

/**
 * The ONE place that decides how confident a card may LOOK.
 *
 *   "deal"     — strict signal, no caveat → strong colored badge.
 *   "estimate" — soft signal on a tight cohort → transparent chip with
 *                a subtle colored outline + explicit "Schätzung" tag.
 *   "neutral"  — caveat, weak cohort, broad cohort or a backend-blocked
 *                count → NEVER a green deal promise.
 *
 * A signal the engine deliberately suppressed (kill switch, weak
 * cohort, broad-basis fallback) can therefore not re-enter the UI as a
 * deal through styling. Returns `null` when there is nothing to show.
 */
export const TIER_DEAL = "deal";
export const TIER_ESTIMATE = "estimate";
export const TIER_NEUTRAL = "neutral";

export function valuationTier(card) {
  if (!card) return null;
  if (card.market_signal_caveat) return TIER_NEUTRAL;
  if (card.market_signal) return TIER_DEAL;
  if (!card.market_signal_soft) return null;
  return isWeakCohortContext(card) ? TIER_NEUTRAL : TIER_ESTIMATE;
}

/** Variants that carry a green "this is a bargain" promise. */
export const GREEN_VARIANTS = new Set([
  "green-strong", "green", "green-estimate",
]);

/* ----------------- decision tree (single place) ----------------- */

/**
 * Returns `{ kind, tier, label, variant, tooltip, estimate }` for a
 * DealCard, or `null` when nothing should be rendered. `kind` is one of:
 *   - "caveat" — neutral info chip, never percent
 *   - "strict" — colored badge with exact percent
 *   - "soft"   — estimate chip with optional percent
 *
 * `opts.full` (default false) switches caveat + soft labels to the
 * verbose DetailPage wording.
 */
export function resolveMarketBadge(card, opts = {}) {
  if (!card) return null;
  const full = opts.full === true;
  const {
    market_signal,
    market_signal_soft,
    market_signal_caveat,
    price_vs_market_pct,
    confidence_band_key,
    confidence_label,
    confidence_score,
  } = card;

  // Display contract — a BLOCKED comparable count must never resurface
  // in the tooltip (the brand-only "Basis: 487 Inserate" case).
  const bandValue = confidence_band_key ?? confidence_label ?? confidence_score;
  const shownCount = displayComparableCount(card);
  const baseTooltip =
    shownCount === null && isWeakCohortContext(card)
      ? t("tooltipBasisBlocked", { band: confidenceBand(bandValue) })
      : buildTooltip(shownCount, bandValue);
  const tier = valuationTier(card);

  // 1. Caveat — highest priority. Neutral (never green), NO percentage.
  if (market_signal_caveat) {
    const subtitle = resolveSubtitle(card);
    const label = full
      ? formatCaveatLabelFull(market_signal_caveat, subtitle)
      : formatCaveatLabelCompact(market_signal_caveat);
    // Card-mode tooltip carries the subtitle so the verbose info isn't lost.
    const tooltipParts = [baseTooltip];
    if (!full && subtitle) tooltipParts.push(subtitle);
    return {
      kind: "caveat",
      tier,
      variant: "neutral-caution",
      label,
      estimate: false,
      tooltip: tooltipParts.join("\n"),
    };
  }

  // 2. Strict signal — bold, includes percentage. The only deal promise.
  if (market_signal) {
    return {
      kind: "strict",
      tier,
      variant: strictVariant(market_signal),
      label: formatStrictLabel(market_signal, price_vs_market_pct),
      estimate: false,
      tooltip: baseTooltip,
    };
  }

  // 3. Soft only — explicit estimate. Green outline ONLY on a tight
  //    cohort; weak/broad basis falls back to neutral.
  if (market_signal_soft) {
    return {
      kind: "soft",
      tier,
      variant:
        tier === TIER_ESTIMATE
          ? softEstimateVariant(market_signal_soft)
          : "neutral-estimate",
      label: formatSoftLabel(
        market_signal_soft, price_vs_market_pct, { compact: !full },
      ),
      estimate: true,
      tooltip: baseTooltip,
    };
  }
  return null;
}

function strictVariant(signal) {
  switch (signal) {
    case "strong_under_market": return "green-strong";
    case "under_market":        return "green";
    case "fair":                return "neutral-gray";
    case "over_market":         return "orange";
    case "strongly_over_market":return "red";
    default:                    return "neutral-gray";
  }
}

/** Tight-cohort soft signal → transparent chip with a colored outline. */
function softEstimateVariant(soft) {
  if (!soft) return "neutral-estimate";
  if (soft.startsWith("under_market")) return "green-estimate";
  if (soft.startsWith("over_market")) return "orange-estimate";
  return "neutral-estimate";
}

/* ----------------- detail-page explainer ----------------- */

/**
 * Plain-language answer to "why does this listing look like this?".
 * Returns `{ tier, basis, confidence, reason, noDealHint }` or `null`.
 * Used by the web + native detail screens so the wording stays in sync.
 */
export function buildValuationExplainer(card) {
  const tier = valuationTier(card);
  if (!tier) return null;
  const scope = cohortShortLabel(card.cohort_level_name) || t("cohort.fallback");
  const count = displayComparableCount(card);
  const countAllowed = count !== null;

  const broadCohort =
    BROAD_COHORT_LEVELS.has(card.cohort_level_name || "")
    || card.market_signal_subtitle_key === "broad_cohort_conservative";
  const basis =
    countAllowed && count > 0
      ? count === 1
        ? t("explainer.basisCountOne", { scope })
        : t("explainer.basisCount", { count, scope })
      : broadCohort
        ? t("explainer.basisBroad", { scope })
        : t("explainer.basisThin", { scope });

  const confidence = confidenceBand(
    card.confidence_band_key ?? card.confidence_label ?? card.confidence_score,
  );

  let reasonKey = "reasonThinData";
  if (tier === TIER_DEAL) reasonKey = "reasonDeal";
  else if (tier === TIER_ESTIMATE) reasonKey = "reasonEstimate";
  // 11.10.2026 — belastete Verkäufe: die Kohorte kennt keine
  // Belastungsdimension, der Abstand ist die Bindung und kein Vorteil.
  else if (card.market_signal_caveat === "tenanted_no_cohort") {
    reasonKey = "reasonTenanted";
  } else if (card.market_signal_caveat === "subsidised_no_cohort") {
    reasonKey = "reasonSubsidised";
  } else if (card.market_signal_caveat === "similar_cohort_basis") {
    reasonKey = "reasonSimilarBasis";
  } else if (broadCohort) {
    reasonKey = "reasonBroadCohort";
  }

  return {
    tier,
    basis,
    confidence,
    reason: t(`explainer.${reasonKey}`, { scope }),
    noDealHint: tier === TIER_NEUTRAL ? t("explainer.noDealHint") : null,
  };
}
